import { ADMIN_PANEL_CLIENT_ID } from 'constants/auth';
import { getDomainName } from 'utils/domain';

import { SKIP_FORBIDDEN } from './httpConfig';

// Company-view collection section path segments. Mirrors the cert profile's
// per-section editing endpoints, but rooted on the SubCompany (the single
// source) rather than the cert draft.
const COMPANY_PROFILE_SECTION_PATHS = {
  owners: 'owners',
  affiliates: 'affiliates',
  investors: 'investors',
  warehouseLines: 'warehouse-lines',
  deliveryChannels: 'delivery-channels',
  dbas: 'dbas',
};

export class SubCompaniesApi {
  #http;

  constructor(http) {
    this.#http = http;
  }

  async getSubCompanies(params) {
    return this.#http.get('/api/sub-companies', { params });
  }

  async getSubCompanyById(id) {
    return this.#http.get(`/api/sub-companies/${id}`);
  }

  async createSubCompany(body) {
    return this.#http.post('/api/sub-companies', body);
  }

  async updateSubCompany({ id, ...body }) {
    return this.#http.put(`/api/sub-companies/${id}`, body);
  }

  async deleteSubCompany(id) {
    return this.#http.delete(`/api/sub-companies/${id}`);
  }

  async getSubCompanyUsers({ subCompanyId, includeDescendants = true }) {
    return this.#http.get(`/api/sub-companies/${subCompanyId}/users`, {
      params: { includeDescendants },
    });
  }

  async inviteSubCompanyUser({ subCompanyId, firstName, lastName, email, role }) {
    return this.#http.post(
      `/api/sub-companies/${subCompanyId}/users/invite`,
      { firstName, lastName, email, role },
      { headers: { clientId: ADMIN_PANEL_CLIENT_ID, domain: getDomainName() } },
    );
  }

  // Company resolves from the AuthorizationContext server-side (mirrors the other sub-company
  // allowed-roles endpoints) — no companyId in the path. Returns { allowedClaims: string[] }, the
  // ceiling a broker-owner can grant their own team's security groups.
  async getSubCompanyClaimCeiling({ subCompanyId }) {
    // Degradable broker read: skip the global forbidden-status page-reload interceptor so a failed
    // ceiling fetch shows an in-panel error instead of reloading the whole app.
    return this.#http.get(`/api/sub-companies/${subCompanyId}/claim-ceiling`, {
      skipResponseInterceptor: { forbiddenError: true },
    });
  }

  // DEV-4535 finalized contract: the seeded-vs-custom + admin-lock flags for the broker Security
  // Groups list come from this DEDICATED endpoint, not the generic roles list. Returns
  // [{ roleId, roleName, isSeededFromTemplate, isLocked }].
  async getSubCompanyAllowedRolesDetails({ subCompanyId }) {
    // Degradable broker read: skip the global forbidden-status page-reload interceptor so a failed
    // allowed-roles fetch shows an in-list error instead of reloading the whole app.
    return this.#http.get(`/api/sub-companies/${subCompanyId}/allowed-roles/details`, {
      skipResponseInterceptor: { forbiddenError: true },
    });
  }

  // DEV-4535 finalized contract: replaces the user's FULL security-group membership set (the stack
  // of check-on/off groups), not an add/remove delta.
  async updateSubCompanyUserSecurityGroups({ subCompanyId, userId, roleIds }) {
    return this.#http.put(`/api/sub-companies/${subCompanyId}/users/${userId}/security-groups`, {
      roleIds,
    });
  }

  // DEV-3611: the SubCompany is the single source for all company-level data
  // (identity, agency, owners, comp, channels). This projects the SubCompany's
  // company-level groups into the flat company-view the editor binds to — the
  // same shape the cert wizard reads, minus the cert-cycle fields. The cert
  // profile is no longer read for company-level data.
  async getCompanyProfile(id, { skipForbidden = false } = {}) {
    return this.#http.get(
      `/api/sub-companies/${id}/company-profile`,
      skipForbidden ? SKIP_FORBIDDEN : {},
    );
  }

  async updateCompanyProfileSection({ id, section, body }) {
    return this.#http.put(
      `/api/sub-companies/${id}/company-profile/${COMPANY_PROFILE_SECTION_PATHS[section]}`,
      body,
    );
  }

  async updateSubCompanyBranding({ id, branding }) {
    return this.#http.put(`/api/sub-companies/${id}/branding`, branding);
  }

  async uploadSubCompanyImage({ id, type, formData }) {
    return this.#http.post(`/api/sub-companies/${id}/images/${type}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }
}
